import { useTranslation } from "@/i18n"
import type { ActiveFilter } from "@/nostr/api/filters/filterEngine"
import { Icon } from "@/components/Icon"

export interface ActiveFilterChipsProps {
  filters: ActiveFilter[]
  onRemove: (key: string) => void
  onClearAll: () => void
  className?: string
}

/**
 * The active filters as removable chips.
 *
 * They mirror the state exactly (one chip per selection), so a reader can always
 * tell why a post is missing and undo a single filter without opening the panel.
 */
export function ActiveFilterChips({
  filters,
  onRemove,
  onClearAll,
  className = "",
}: ActiveFilterChipsProps) {
  const { t } = useTranslation()
  if (filters.length === 0) return null

  return (
    <div className={`active-filter-chips ${className}`.trim()} role="list" aria-label={t("Active filters")}>
      {filters.map((filter) => (
        <span key={filter.key} className="chip filter-chip active active-filter-chip" role="listitem">
          {t(filter.label, filter.vars)}
          <button
            type="button"
            className="active-filter-chip-remove"
            onClick={() => onRemove(filter.key)}
            aria-label={t("Remove filter: {label}", { label: t(filter.label, filter.vars) })}
          >
            <Icon name="x" size={12} />
          </button>
        </span>
      ))}
      <button type="button" className="btn btn-ghost btn-small filter-clear-all" onClick={onClearAll}>
        {t("Clear all")}
      </button>
    </div>
  )
}
